// Format tampilan: rupiah dari string BigInt (locale id-ID, `Rp1.500`) dan waktu WIB.
const rupiah = new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 });

export function formatRupiah(amount: string | bigint): string {
  const value = typeof amount === 'bigint' ? amount : BigInt(amount);
  const sign = value < 0n ? '-' : '';
  return `${sign}Rp${rupiah.format(value < 0n ? -value : value)}`;
}

/** Nominal tanpa awalan "Rp" (untuk tampilan "Rp" kecil terpisah): "250.000". */
export function formatAmount(amount: string | bigint): string {
  const value = typeof amount === 'bigint' ? amount : BigInt(amount);
  return rupiah.format(value);
}

/** Rupiah ringkas untuk kartu statistik: 125.000.000 → "Rp 125 JT", 1.500.000.000 → "Rp 1,5 M". */
export function formatCompactRupiah(amount: string | bigint): string {
  const value = typeof amount === 'bigint' ? amount : BigInt(amount);
  const units: [bigint, string][] = [
    [1_000_000_000_000n, 'T'],
    [1_000_000_000n, 'M'],
    [1_000_000n, 'JT'],
    [1_000n, 'RB'],
  ];
  for (const [unit, label] of units) {
    if (value >= unit) {
      // Satu angka desimal, dibulatkan ke bawah (tampilan saja, bukan perhitungan uang).
      const tenths = (value * 10n) / unit;
      const whole = tenths / 10n;
      const fraction = tenths % 10n;
      return `Rp ${rupiah.format(whole)}${fraction > 0n ? `,${fraction}` : ''} ${label}`;
    }
  }
  return `Rp ${rupiah.format(value)}`;
}

const shortDate = new Intl.DateTimeFormat('id-ID', {
  timeZone: 'Asia/Jakarta',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
/** "12 Okt 2026" (WIB). */
export const formatShortDate = (iso: string): string => shortDate.format(new Date(iso)).replace('.', '');

const dateTime = new Intl.DateTimeFormat('id-ID', {
  timeZone: 'Asia/Jakarta',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});
const dateOnly = new Intl.DateTimeFormat('id-ID', {
  timeZone: 'Asia/Jakarta',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

export const formatDateTime = (iso: string): string => `${dateTime.format(new Date(iso))} WIB`;
export const formatDate = (iso: string): string => dateOnly.format(new Date(iso));

/** "12.5" → "12,5%". */
export const formatPercent = (value: string): string => `${value.replace('.', ',')}%`;

/** Tanggal kalender (YYYY-MM-DD, zona WIB) → awal/akhir hari dalam ISO. */
export const wibStartOfDay = (date: string): string => new Date(`${date}T00:00:00+07:00`).toISOString();
export const wibEndOfDay = (date: string): string => new Date(`${date}T23:59:59.999+07:00`).toISOString();

/** "0812…" / "62812…" / "+62 812-…" → "+62812…". */
export function normalizePhone(input: string): string {
  const digits = input.replace(/[^\d+]/g, '');
  if (digits.startsWith('+')) return digits;
  if (digits.startsWith('62')) return `+${digits}`;
  if (digits.startsWith('0')) return `+62${digits.slice(1)}`;
  // Tanpa awalan, mis. "81234567890" (seperti di form registrasi dengan prefiks +62 terpisah).
  if (digits.startsWith('8')) return `+62${digits}`;
  return digits;
}

/** Hanya digit untuk input nominal: "1.500.000" → "1500000". */
export const digitsOnly = (input: string): string => input.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
